/**
 * Your data: a download of everything in the workspace, photos included, to keep.
 *
 * The full export PRODUCT.md promises before anything is deleted by a downgrade, a cancellation
 * or closing the account; those flows send the user here. It is also open at any other time, and
 * to a read-only workspace, whose records are its own to take away. The worker builds it, so the
 * list below polls until it is ready, as the accountant's export does.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/problem";
import { Button, Notice } from "@/components/ui/Field";
import { meQuery } from "@/features/auth/session";
import { photosQuery, useDeletePhotos } from "@/features/receipts/queries";
import { ExportRow } from "@/features/reports/ExportRow";
import { exportsQuery, useRequestExport } from "@/features/reports/queries";
import { SettingsHeader } from "@/features/settings/SettingsHeader";

export function DataPage() {
  const { t } = useTranslation();
  const me = useQuery(meQuery);
  const exports = useQuery(exportsQuery("full"));
  const request = useRequestExport();
  const making = (exports.data ?? []).some(
    (item) => item.status === "queued" || item.status === "running",
  );
  const keepsPhotos = me.data?.workspace?.keeps_receipt_images ?? false;
  const kept = useQuery(photosQuery).data?.kept ?? 0;

  return (
    <section className="space-y-5">
      <SettingsHeader title={t("data.title")} description={t("data.subtitle")} />

      <PhotosAwaiting />

      <div className="space-y-3 rounded-lg border border-stone-200 bg-white p-4">
        <p className="text-stone-800">{t("data.contents")}</p>
        <p className="text-sm text-stone-600">
          {kept > 0
            ? t("data.photosIncluded", { count: kept })
            : keepsPhotos
              ? t("data.photosKept")
              : t("data.photosDeleted")}
        </p>
        {request.error && (
          <Notice tone="error">
            {request.error instanceof ApiError ? request.error.message : t("data.failed")}
          </Notice>
        )}
        <Button
          type="button"
          disabled={request.isPending || making}
          onClick={() => request.mutate({ kind: "full" })}
        >
          {making ? t("data.making") : t("data.make")}
        </Button>
      </div>

      {exports.isError && <Notice tone="error">{t("reports.export.errors.loadFailed")}</Notice>}
      {exports.data && exports.data.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-medium text-stone-800">{t("data.recent")}</h2>
          <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
            {exports.data.map((item) => (
              <ExportRow key={item.id} item={item} />
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-stone-200 pt-5">
        <Link to="/settings/close-account" className="text-sm font-medium text-red-700 underline">
          {t("close.title")}
        </Link>
      </div>
    </section>
  );
}

/**
 * Photos kept on a plan with retention that the current plan does not keep: what a downgrade
 * leaves. PRODUCT.md §8 promises the full download first, so deleting them is offered once a full
 * export has been downloaded, or, if the owner says so and confirms it, without one.
 */
function PhotosAwaiting() {
  const { t } = useTranslation();
  const photos = useQuery(photosQuery);
  const remove = useDeletePhotos();
  const [skipping, setSkipping] = useState(false);
  const [understood, setUnderstood] = useState(false);

  const count = photos.data?.awaiting_deletion ?? 0;
  if (remove.isSuccess) {
    return (
      <Notice tone="success">{t("data.photos.deleting", { count: remove.data.queued })}</Notice>
    );
  }
  if (count === 0) return null;
  const downloaded = photos.data?.export_downloaded ?? false;

  return (
    <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <h2 className="font-medium text-stone-900">{t("data.photos.title")}</h2>
      <p className="text-sm text-stone-700">{t("data.photos.explain", { count })}</p>
      {downloaded ? (
        <Button disabled={remove.isPending} onClick={() => remove.mutate(false)}>
          {t("data.photos.delete", { count })}
        </Button>
      ) : (
        <p className="text-sm font-medium text-stone-800">{t("data.photos.downloadFirst")}</p>
      )}
      {remove.error && (
        <Notice tone="error">
          {remove.error instanceof ApiError ? remove.error.message : t("data.failed")}
        </Notice>
      )}
      {!downloaded &&
        (skipping ? (
          <div className="space-y-3 border-t border-amber-200 pt-3">
            <label className="flex items-start gap-3 text-sm text-stone-800">
              <input
                type="checkbox"
                className="mt-0.5 h-5 w-5 shrink-0"
                checked={understood}
                onChange={(event) => setUnderstood(event.target.checked)}
              />
              {t("data.photos.understand", { count })}
            </label>
            <Button
              className="bg-red-700 hover:bg-red-800"
              disabled={!understood || remove.isPending}
              onClick={() => remove.mutate(true)}
            >
              {t("data.photos.deleteWithout", { count })}
            </Button>
          </div>
        ) : (
          <button
            type="button"
            className="text-sm text-stone-600 underline"
            onClick={() => setSkipping(true)}
          >
            {t("data.photos.skip")}
          </button>
        ))}
    </div>
  );
}
